import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {EMPTY, finalize, forkJoin, switchMap, tap, timer} from 'rxjs';
import {catchError, exhaustMap} from 'rxjs/operators';
import {MaterialModule} from '../../../material.module';
import {
    ConnectIntegrationsService,
    IntegrationDetail,
    IntegrationRun,
    IntegrationValidationError,
    IntegrationValidationOperation,
    SyntheticTransferConfiguration,
} from '../../../services/connect/connect-integrations.service';

function configurationValidator(control: AbstractControl): ValidationErrors | null {
    const config = control.value as SyntheticTransferConfiguration;
    if ([config.recordCount, config.multiplier, config.failAtRecord].some(value => !Number.isInteger(value))) {
        return {integerRequired: true};
    }
    return config.failAtRecord > config.recordCount ? {failureOutsideRange: true} : null;
}

@Component({
    selector: 'app-connect-integrations',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MaterialModule],
    templateUrl: './connect-integrations.component.html',
    styleUrls: ['../connect-shared.scss', './connect-integrations.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {class: 'connect-theme'},
})
export class ConnectIntegrationsComponent implements OnInit {
    private readonly api = inject(ConnectIntegrationsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(FormBuilder);
    private pollRemaining = 0;
    private pendingRunKey: string | null = null;
    private pendingValidationKey: string | null = null;

    @Input() installationId: string | null = null;

    readonly local = this.route.snapshot.data['localIntegration'] === true;
    readonly supportedUiKey = 'synthetic-transfer';
    readonly integration = signal<IntegrationDetail | null>(null);
    readonly runs = signal<IntegrationRun[]>([]);
    readonly loading = signal(false);
    readonly action = signal<string | null>(null);
    readonly error = signal<string | null>(null);
    readonly notice = signal<string | null>(null);
    readonly validationErrors = signal<IntegrationValidationError[]>([]);
    readonly validatedRevision = signal<number | null>(null);
    readonly empty = signal(false);
    readonly draftChanged = signal(false);
    readonly validation = signal<IntegrationValidationOperation | null>(null);

    readonly form = this.fb.nonNullable.group({
        recordCount: [5, [Validators.required, Validators.min(1), Validators.max(50)]],
        multiplier: [2, [Validators.required, Validators.min(1), Validators.max(10)]],
        failAtRecord: [0, [Validators.required, Validators.min(0), Validators.max(50)]],
    }, {validators: configurationValidator});

    ngOnInit(): void {
        this.refresh();
        timer(2000, 2000).pipe(
            exhaustMap(() => {
                const integration = this.integration();
                if (!integration || this.pollRemaining <= 0 || this.loading() || this.action()) return EMPTY;
                this.pollRemaining--;
                return forkJoin({detail: this.api.get(integration.id), runs: this.api.listRuns(integration.id)}).pipe(
                    tap(result => {
                        this.receiveDetail(result.detail);
                        this.runs.set(result.runs.items);
                        if (!result.detail.running && !this.validationPending() && !result.runs.items.some(run => ['queued', 'running'].includes(run.status))) {
                            this.pollRemaining = 0;
                        }
                    }),
                    catchError(() => {
                        this.pollRemaining = 0;
                        this.error.set('Status could not be refreshed. Use Refresh to try again.');
                        return EMPTY;
                    }),
                );
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    refresh(): void {
        if (this.loading() || this.action()) return;
        this.loading.set(true);
        this.error.set(null);
        const source = this.installationId ? forkJoin({detail: this.api.get(this.installationId), runs: this.api.listRuns(this.installationId)}) : this.api.list().pipe(
            switchMap(result => {
                const current = this.integration();
                const item = result.items.find(candidate => candidate.id === current?.id) ?? result.items[0];
                this.empty.set(!item);
                if (!item) return EMPTY;
                return forkJoin({detail: this.api.get(item.id), runs: this.api.listRuns(item.id)});
            }),
        );
        source.pipe(
            finalize(() => this.loading.set(false)),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: result => {
                this.receiveDetail(result.detail);
                this.runs.set(result.runs.items);
                if (result.detail.running) this.pollRemaining = 30;
            },
            error: err => this.handleError(err),
        });
    }

    saveDraft(): void {
        const integration = this.integration();
        this.form.markAllAsTouched();
        if (!integration || !this.canConfigure() || this.form.invalid || this.draftChanged() || this.action() || this.loading()) return;
        this.startAction('save');
        this.api.saveDraft(integration.id, integration.draftRevision, this.form.getRawValue()).pipe(
            finalize(() => this.finishAction()), takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: detail => {
                this.integration.set(detail);
                this.loadForm(detail);
                this.validatedRevision.set(null);
                this.pendingValidationKey = null;
                this.validation.set(null);
                this.notice.set('Draft saved. Validate it before making it active.');
            },
            error: err => this.handleError(err),
        });
    }

    validateDraft(): void {
        const integration = this.integration();
        if (!integration || !this.canValidate()) return;
        this.startAction('validate');
        if (integration.validationMode === 'async') {
            this.pendingValidationKey ??= crypto.randomUUID();
            this.api.validateHosted(integration.id, integration.draftRevision, this.pendingValidationKey).pipe(
                finalize(() => this.finishAction()), takeUntilDestroyed(this.destroyRef),
            ).subscribe({
                next: result => {
                    this.pendingValidationKey = null;
                    this.receiveValidation(result.validation);
                    this.integration.update(detail => detail ? {...detail, latestValidation: result.validation,
                        running: ['queued', 'running'].includes(result.validation.status)} : null);
                    this.pollRemaining = this.validationPending() ? 90 : 0;
                    if (this.validationPending()) this.notice.set('Validation requested. This page will update when the integration finishes checking your settings.');
                },
                error: err => {
                    if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500 && err.status !== 408) this.pendingValidationKey = null;
                    this.handleError(err);
                },
            });
            return;
        }
        this.api.validate(integration.id, integration.draftRevision).pipe(
            finalize(() => this.finishAction()), takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: result => {
                this.validationErrors.set(result.errors);
                this.validatedRevision.set(result.valid ? result.revision : null);
                this.notice.set(result.valid ? 'Validation passed. This draft is ready to activate.' : null);
                if (!result.valid) this.error.set('This draft needs changes before it can be activated.');
            },
            error: err => this.handleError(err),
        });
    }

    activateDraft(): void {
        const integration = this.integration();
        if (!integration || !this.canActivate()) return;
        this.startAction('activate');
        this.api.activate(integration.id, integration.draftRevision).pipe(
            finalize(() => this.finishAction()), takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: detail => {
                this.integration.set(detail);
                this.notice.set(`Revision ${detail.activeRevision} is active. New runs will use these settings.`);
            },
            error: err => this.handleError(err),
        });
    }

    runOnce(): void {
        const integration = this.integration();
        if (!integration?.activeRevision || !this.canRun()) return;
        this.startAction('run');
        this.pendingRunKey ??= crypto.randomUUID();
        this.api.runOnce(integration.id, integration.activeRevision, this.pendingRunKey).pipe(
            finalize(() => this.finishAction()), takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: result => {
                this.pendingRunKey = null;
                this.runs.update(runs => [result.run, ...runs.filter(run => run.id !== result.run.id)]);
                this.integration.update(detail => detail ? {...detail, running: ['queued', 'running'].includes(result.run.status)} : null);
                this.pollRemaining = 30;
                this.notice.set('Run requested. Its progress appears below.');
            },
            error: err => {
                if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500 && err.status !== 408) {
                    this.pendingRunKey = null;
                }
                this.handleError(err);
            },
        });
    }

    discardChanges(): void {
        const integration = this.integration();
        if (integration) this.loadForm(integration);
    }

    canActivate(): boolean {
        const integration = this.integration();
        return !!integration && this.canConfigure() && !this.form.dirty && !this.draftChanged() && !this.action() && !this.loading()
            && this.validatedRevision() === integration.draftRevision
            && integration.activeRevision !== integration.draftRevision;
    }

    statusLabel(): string {
        const integration = this.integration();
        if (integration?.running) return 'Running';
        if (integration && integration.runtimeAvailable !== true) return 'Runtime unavailable';
        return integration?.activeRevision ? 'Ready to run' : 'Configuration needed';
    }

    hasSupportedView(): boolean {
        const item = this.integration();
        return item?.uiKey === this.supportedUiKey && (!this.installationId || item.id === this.installationId)
            && (item.runtimeProfile === 'synthetic-transfer.v1' || this.local && item.runtimeProfile === undefined);
    }

    canConfigure(): boolean {
        const item = this.integration();
        return this.hasSupportedView() && item?.capabilities?.configure === true
            && (item.validationMode === 'async' || this.local && item.validationMode === 'inline');
    }

    canRun(): boolean {
        const item = this.integration();
        return this.hasSupportedView() && item?.capabilities?.run === true && item.runtimeAvailable === true
            && (item.validationMode === 'async' || this.local && item.validationMode === 'inline')
            && !!item.activeRevision && !item.running && !this.action() && !this.loading();
    }

    canValidate(): boolean {
        const item = this.integration();
        return this.canConfigure() && item?.runtimeAvailable === true && !item.running && !this.validationPending()
            && !this.form.dirty && !this.draftChanged() && !this.action() && !this.loading();
    }

    validationPending(): boolean {
        return ['queued', 'running'].includes(this.validation()?.status ?? '');
    }

    runIssue(code: string | null | undefined): string {
        if (code === 'SYNTHETIC_FAILURE') return 'Stopped at the configured failure record. Change the test settings and run again.';
        if (code === 'runtime.timeout') return 'The run exceeded its time limit.';
        if (code === 'JOB_TIMED_OUT' || code === 'LEASE_EXPIRED') return 'The run exceeded its time limit. Refresh its status before requesting another run.';
        if (code === 'WINDOW_CLOSED') return 'The beta runtime is asleep. Run again during an active beta session.';
        if (code === 'DISPATCH_UNCONFIRMED') return 'Execution could not be confirmed. Review its status before requesting another run.';
        if (code === 'runtime.interrupted') return 'The service restarted before this run completed. Review its status before running again.';
        return code ? 'The run did not complete. Review the settings and try again.' : '';
    }

    private receiveDetail(detail: IntegrationDetail): void {
        const previousRevision = this.integration()?.draftRevision;
        if (previousRevision !== detail.draftRevision) {
            this.validatedRevision.set(null);
            this.pendingValidationKey = null;
            if (this.form.dirty) this.draftChanged.set(true);
        }
        this.integration.set(detail);
        if (detail.validationMode === 'async' && detail.latestValidation) this.receiveValidation(detail.latestValidation);
        if (!this.form.dirty && this.hasSupportedView()) this.loadForm(detail);
        this.syncFormAccess();
    }

    private receiveValidation(operation: IntegrationValidationOperation): void {
        this.validation.set(operation);
        if (operation.configurationRevision !== this.integration()?.draftRevision || this.validationPending()) return;
        this.validationErrors.set(operation.errors);
        this.validatedRevision.set(operation.status === 'succeeded' && operation.valid === true ? operation.configurationRevision : null);
        if (operation.status === 'succeeded' && operation.valid === true) this.notice.set('Validation passed. This draft is ready to activate.');
        else if (operation.status === 'succeeded') this.error.set('This draft needs changes before it can be activated.');
        else this.error.set('Validation could not finish. Refresh its status and try again when the runtime is available.');
    }

    private loadForm(detail: IntegrationDetail): void {
        this.form.reset(detail.draft.configuration);
        this.draftChanged.set(false);
    }

    private startAction(action: string): void {
        this.action.set(action);
        this.form.disable({emitEvent: false});
        this.error.set(null);
        this.notice.set(null);
        this.validationErrors.set([]);
    }

    private finishAction(): void {
        this.action.set(null);
        this.syncFormAccess();
    }

    private syncFormAccess(): void {
        if (this.canConfigure() && !this.action()) this.form.enable({emitEvent: false});
        else this.form.disable({emitEvent: false});
    }

    private handleError(err: unknown): void {
        const response = err instanceof HttpErrorResponse ? err : null;
        const code = response?.error?.error?.code;
        if (code === 'history_capacity_reached') {
            this.error.set('This beta integration has reached its history limit. An administrator needs to extend its ledger before another run.');
        } else if (code === 'run_in_progress') {
            this.error.set('An operation is already in progress. Refresh its status before trying again.');
        } else if (code === 'validation_required') {
            this.validatedRevision.set(null);
            this.error.set('Validate this saved revision before making it active.');
        } else if (response?.status === 401 || response?.status === 403) {
            this.error.set('Your account cannot perform this action. Refresh your access or contact an administrator.');
        } else if (code === 'revision_conflict' || response?.status === 409) {
            this.validatedRevision.set(null);
            this.error.set('The configuration changed in another session. Refresh, review the current draft, and save your changes again.');
        } else if (code === 'configuration_invalid') {
            this.validatedRevision.set(null);
            this.validationErrors.set(response?.error?.error?.validationErrors ?? []);
            this.error.set('This configuration could not be validated. Your active settings have not changed.');
        } else if (response?.status === 404) {
            this.error.set(this.local ? 'Integrations are not available on this server. Start the local integration service to use this view.'
                : 'This integration is not available for the selected account. Refresh the view or contact an administrator.');
        } else {
            this.error.set('The integration service could not complete this request. Refresh its status before trying again.');
        }
    }
}
